import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiArrowRight, FiTrendingUp, FiClock, FiDollarSign, FiUsers } from 'react-icons/fi';
import api from '../utils/api';

const ICON_MAP = {
  FiTrendingUp,
  FiClock,
  FiDollarSign,
  FiUsers,
};

function parseCaseStudy(row) {
  const data = JSON.parse(row.data || '{}');
  const tags = JSON.parse(row.tags || '[]');
  const metrics = (data.metrics || []).map(m => ({
    ...m,
    icon: ICON_MAP[m.icon] || FiTrendingUp,
  }));
  return {
    id: row.id,
    slug: row.slug,
    status: row.status,
    company: row.company,
    role: row.role,
    duration: row.duration,
    tags,
    headline: row.headline,
    summary: row.summary,
    metrics,
    sections: data.sections || [],
  };
}

function MetricPill({ icon: Icon, label, value, delta }) {
  return (
    <div className="flex flex-col items-center text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400 mb-2" />
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500">{delta}</p>
    </div>
  );
}

function CaseStudyCard({ study, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const navigate = useNavigate();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.05, duration: 0.2 }}
      className="card-modern overflow-hidden cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
      onClick={() => study.slug && navigate(`/projects/${study.slug}`)}
    >
      <div className="p-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {study.company}
              </span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{study.role}</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{study.duration}</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {study.headline}
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {study.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>

        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{study.summary}</p>

        {study.metrics.length > 0 && (
          <div className={`grid gap-4 mb-6 ${study.metrics.length === 1 ? 'grid-cols-1 max-w-xs' : study.metrics.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {study.metrics.map((m, i) => (
              <MetricPill key={i} {...m} />
            ))}
          </div>
        )}

        <span className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <span>Read full case study</span>
          <FiArrowRight className="w-4 h-4" />
        </span>
      </div>
    </motion.div>
  );
}

function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/projects');
        const parsed = response.data
          .filter(row => row.type === 'case-study')
          .map(row => parseCaseStudy(row));
        setCaseStudies(parsed);
      } catch (error) {
        console.error('Error loading case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gray-900 dark:text-white">
            Case Studies
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl">
            In-depth looks at product and operations work with measurable outcomes.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-10 h-10 border-4 border-gray-300 dark:border-gray-600 border-t-black dark:border-t-white rounded-full"
            />
          </div>
        ) : (
          <div className="space-y-8">
            {caseStudies.map((study, index) => (
              <CaseStudyCard key={study.id} study={study} index={index} />
            ))}
            {caseStudies.length === 0 && (
              <p className="text-center text-gray-400 dark:text-gray-500 py-12">No case studies yet.</p>
            )}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Facing a product or operations challenge?
          </p>
          <Link to="/wait-works" className="btn-modern inline-flex items-center space-x-2">
            <span>Work With Me</span>
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

export default CaseStudies;
